/**
 * Serializes user-data sync work (reconnect reconcile + push to server) so list(),
 * login, and connectivity handlers do not run overlapping full sync stacks.
 */

import {
  runReconnectSync,
  syncAllCharactersToServer,
  syncLocalCampaignsToServer,
  buildCharacterLocalToServerIdMap,
  clearMetaCache,
  entityApi,
} from './entityClient.js';
import { syncHomebrewAfterUserDataOnline } from '@/utils/homebrewStore.js';
import { syncLocalSpellbooksToServer } from './spellbookRepository.js';
import { invalidatePostAuthQueries } from '@/utils/queryClientSingleton.js';

let syncChain = Promise.resolve();

function debugUserDataSync(_step, _detail) {
  void _step;
  void _detail;
}

/**
 * @param {() => Promise<void>} fn
 * @returns {Promise<void>}
 */
export function enqueueUserDataSync(fn) {
  const run = () => Promise.resolve().then(fn);
  const next = syncChain.then(run, run);
  syncChain = next.catch(() => {});
  return next;
}

/**
 * After login or when the browser comes back online.
 * @param {{ afterOfflineTransition?: boolean }} options - when true, run reconnect reconcile first (local-first / serverAuthoritative off only inside runReconnectSync).
 */
/**
 * Ordered push of local demo data to the server, then hydrate IndexedDB from API lists
 * and invalidate React Query so the UI shows database-backed data.
 */
export async function syncAllLocalUserDataToServerAfterAuth() {
  await syncAllCharactersToServer();
  debugUserDataSync('syncAllCharactersToServer', 'done');
  await syncLocalCampaignsToServer();
  debugUserDataSync('syncLocalCampaignsToServer', 'done');
  await syncHomebrewAfterUserDataOnline();
  debugUserDataSync('syncHomebrewAfterUserDataOnline', 'done');
  const charMap = await buildCharacterLocalToServerIdMap();
  await syncLocalSpellbooksToServer(charMap);
  debugUserDataSync('syncLocalSpellbooksToServer', 'done');
  clearMetaCache();
  try {
    await entityApi.entities.Character.list();
    await entityApi.entities.Campaign.list();
  } catch {
    // non-fatal — lists will refetch from UI
  }
  invalidatePostAuthQueries();
  debugUserDataSync('invalidatePostAuthQueries', 'done');
}

export function syncUserDataAfterOnlineOrLogin(options = {}) {
  const { afterOfflineTransition = false } = options;
  return enqueueUserDataSync(async () => {
    debugUserDataSync('start', { afterOfflineTransition });
    if (afterOfflineTransition) {
      await runReconnectSync();
      debugUserDataSync('runReconnectSync', 'done');
    }
    await syncAllLocalUserDataToServerAfterAuth();
  });
}
